import { useEffect, useState } from "react";
import { Breadcrumb, Container } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { GetLearningAttemptsQuery } from "api/generated";
import { useGetLearningAttemptsQuery } from "api/generated";
import { Loading } from "components";
import IconArrowBack from "components/Icons/IconArrowBack/IconArrowBack";
import PageHeading from "components/PageHeading/PageHeading";
import { EmptyHistory } from "features/History/EmptyHistory";
import { HistoryList } from "features/History/HistoryList";
import { processApiError } from "utils/processApiError";

const RESULTS_PAGE_SIZE = 3;

const HistoryPage = () => {
  const { t } = useTranslation(["history"]);
  // AIDEV-NOTE: Pages are keyed by page number, not appended: a re-emitted cached page (StrictMode double
  // effect on remount, any future refetch) would otherwise show the same practice twice. `currentData`
  // (not `data`) stays undefined until the CURRENT args arrive, so the old page is never stored under
  // the next page key. Same fix as features/ExamHistory/useExamHistory.ts.
  const [pages, setPages] = useState<Record<number, GetLearningAttemptsQuery["learningAttempts"]>>({});
  const [page, setPage] = useState<number>(1);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [showLoadMoreButton, setShowLoadMoreButton] = useState<boolean>(false);

  const {
    currentData,
    isLoading,
    error: apiError,
  } = useGetLearningAttemptsQuery({
    offset: Math.max(0, page - 1) * RESULTS_PAGE_SIZE,
    limit: RESULTS_PAGE_SIZE,
  });

  useEffect(() => {
    if (apiError === undefined) return;

    processApiError(apiError);
  }, [apiError]);

  useEffect(() => {
    if (currentData === undefined) return;

    setPages(previous => ({ ...previous, [page]: currentData.learningAttempts }));
    setShowLoadMoreButton(currentData.learningAttempts.length === RESULTS_PAGE_SIZE);
    setIsLoadingMore(false);
  }, [currentData, page]);

  // Integer keys iterate in ascending order, so pages come out in page order.
  const loadedData = Object.values(pages).flat();

  const goNextPage = () => {
    if (isLoadingMore || isLoading) return;
    setIsLoadingMore(true);
    setPage(current => current + 1);
  };

  return (
    <>
      <div className={"page-header"}>
        <Container className={"container-mw-md"}>
          <Breadcrumb>
            <li className={"breadcrumb-item"}>
              <Link to={"/"}>
                <IconArrowBack />
                {t("goHome")}
              </Link>
            </li>
          </Breadcrumb>

          <PageHeading className={"mb-8px"} />
        </Container>
      </div>

      <Container className="container-mw-md h-100">
        {isLoading ? (
          <Loading />
        ) : loadedData.length ? (
          <>
            <HistoryList data={loadedData} onLoadMore={goNextPage} />

            {showLoadMoreButton && (
              <div className="w-100 d-flex justify-content-center mt-16px">
                <Button
                  type={"submit"}
                  onClick={() => {
                    goNextPage();
                  }}
                  disabled={isLoading || isLoadingMore}
                >
                  {t("loadMore")}
                </Button>
              </div>
            )}
          </>
        ) : (
          <EmptyHistory />
        )}
      </Container>
    </>
  );
};

export default HistoryPage;
